import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAdminUsers } from '../../hooks/useAdminUsers';
import { useAdminRoles } from '../../hooks/useAdminRoles';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { UsersManagement } from './UsersManagement';
import { RolesManagement } from './RolesManagement';

type AdminTab = 'users' | 'roles' | 'permissions';

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const { users } = useAdminUsers();
  const { roles } = useAdminRoles();
  const { permissions } = useAdminPermissions();
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  // Check if user is admin
  const isAdmin = user?.role?.name === 'ADMIN';
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-lg">
        <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-blue-100">Manage users, roles, and permissions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm font-semibold">Total Users</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">{users.length}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm font-semibold">Total Roles</div>
          <div className="text-3xl font-bold text-purple-600 mt-2">{roles.length}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-gray-600 text-sm font-semibold">Total Permissions</div>
          <div className="text-3xl font-bold text-green-600 mt-2">{permissions.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 py-4 px-6 font-semibold text-center ${
              activeTab === 'users'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            👥 Users Management
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`flex-1 py-4 px-6 font-semibold text-center ${
              activeTab === 'roles'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🔐 Roles Management
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`flex-1 py-4 px-6 font-semibold text-center ${
              activeTab === 'permissions'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            ✓ Permissions
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'users' && <UsersManagement />}
          {activeTab === 'roles' && <RolesManagement />}
          {activeTab === 'permissions' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">Permissions Overview</h2>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(
                  permissions.reduce((acc, perm) => {
                    if (!acc[perm.page]) acc[perm.page] = [];
                    acc[perm.page].push(perm);
                    return acc;
                  }, {} as Record<string, typeof permissions>)
                ).map(([page, perms]) => (
                  <div key={page} className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">{page}</h3>
                    <div className="space-y-2">
                      {perms.map(perm => (
                        <div key={perm.id} className="flex items-center text-sm">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold mr-2">
                            {perm.action}
                          </span>
                          <span className="text-gray-600">{perm.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

